import { LazyIssueApp } from '@jira/issue__lazy-app/src/LazyIssueApp.tsx';
import { MenuIdProjects } from '@jira/navigation-apps__constants/src/MenuIdProjects.tsx';
import { createPageEntryPoint } from '@jira/platform__routing-create-route-entry-point/src/index.tsx';
import { getConsolidationStateResource } from '@jira/router-resources__eoc-tenant-info/src/getConsolidationStateResource.tsx';
import { LazyAtlassianNavigation } from '@jira/navigation-apps__atlassian-navigation/src/async.tsx';
import { SidebarIdCsmApp } from '@jira/navigation-apps__constants/src/SidebarIdCsmApp.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { expVal } from '@atlassian/jira-feature-experiments';
import { getCustomerServiceL1NavigationResources } from '@jira/router-resources__customer-service-l1-navigation/src/getCustomerServiceL1NavigationResources.tsx';
import { createEntry } from '@jira/platform__routing-route-entry/src/createEntry.tsx';
import { notFoundPageEntrypoint } from '@jira/router-routes__not-found-entrypoint/src/ui/error-pages/not-found/index.tsx';
import { embeddedIssuePreloadLayoutBuilder } from '@jira/platform__layouts-issue-preload-layout/src/index.tsx';
import { getIssueViewResources } from '@jira/router-resources__issue-view/src/getIssueViewResources.tsx';
import { horizontalNavQueueResource } from '@jira/navigation-apps__resources/src/controllers/horizontal-navigation/horizontal-nav-queue/index.tsx';
import { composeLayouts } from '@jira/platform__layouts-route-layout/src/GlobalComponent.tsx';
import {
	getInsightResources,
	getQueuesResources,
	getTriageButtonPermissionResource,
} from '@jira/router-resources__service-desk-common/src/getServiceDeskSettingsResources.tsx';
import { horizontalNavQueueEntrypoint } from '@jira/navigation-apps__horizontal-nav-jsm-queue/src/horizontalNavQueueEntrypoint.entrypoint.tsx';
import { issueListSsrResource } from '@jira/router-resources__service-desk-queues/src/queuesNavigationItems.tsx';
import { queuesJqlBuilderHydrationQueryRelayResource } from '@jira/router-resources__service-desk-queues/src/queuesJqlBuilderHydrationQueryRelayResource.tsx';
import { queuesJqlBuilderAiQueryRelayResource } from '@jira/router-resources__service-desk-queues/src/queuesJqlBuilderAiQueryRelayResource.tsx';
import { servicedeskIsItsmProjectResource } from '@jira/router-resources__service-desk-is-itsm-project/src/useIsItsmTemplateProject.tsx';
import { getJsmIssueViewRelatedArticlesRouteResource } from '@jira/router-resources__jsm-issue-view/src/getJsmIssueViewRelatedArticlesRouteResource.tsx';
import { resourceWithCondition } from '@jira/router-resources__utils/src/resourceWithCondition.tsx';
import { csmCustomQueueRoute } from '@jira/router-routes__servicedesk-queue-routes/src/csmCustomQueueRoute.tsx';
import { serviceProjectLayout } from '@jira/platform__layouts-service-project-layout/src/index.tsx';
import { AsyncBulkActionToolbarLazy } from '@jira/servicedesk__bulk-actions-toolbar/src/AsyncBulkActionToolbarLazy.tsx';
import { queuesAgentAndIssueViewEntryPoint } from '@jira/spa-apps__servicedesk-queues-agent-and-issue-view/src/queuesAgentAndIssueViewEntryPoint.entrypoint.tsx';
import type { Route } from '@atlassian/react-resource-router';

import { QueueLoadingSkeleton } from './ui/queue/index.tsx';

const csmCustomQueuePageEntryPoint = createPageEntryPoint({
	main: queuesAgentAndIssueViewEntryPoint,
	horizontalNavigation: horizontalNavQueueEntrypoint,
});

// It is important to keep these in sync:
//   - customQueueRouteEntry
//   - customIssueRouteEntry
//   - csmCustomQueueRouteEntry
//   - csmCustomIssueRouteEntry
// they are rendering the exact same view. This is done to keep the state
// consistent when switching between the two routes.
export const csmCustomQueueRouteEntry: Route = createEntry(csmCustomQueueRoute, () => ({
	group: 'customer-service',
	layout: composeLayouts(
		serviceProjectLayout,
		embeddedIssuePreloadLayoutBuilder([
			{
				params: [
					{
						key: 'issueKey',
					},
				],
			},
		]),
	),
	preloadOptions: {
		earlyChunks: fg('queues-earlyscripts') ? ['queues-prefetch-early-entry'] : [],
	},
	navigation: {
		menuId: MenuIdProjects,
		sidebarId: SidebarIdCsmApp,
	},
	skeleton: QueueLoadingSkeleton,
	resources: [
		...getCustomerServiceL1NavigationResources(),
		issueListSsrResource,
		...getQueuesResources(),
		...getIssueViewResources(),
		...getInsightResources(),
		...getTriageButtonPermissionResource(),
		getConsolidationStateResource(),
		servicedeskIsItsmProjectResource,
		getJsmIssueViewRelatedArticlesRouteResource(),
		horizontalNavQueueResource,
		queuesJqlBuilderHydrationQueryRelayResource,
		resourceWithCondition(
			() => expVal('corcap-6777-ai-jqlbuilder-in-queues', 'isEnabled', false),
			queuesJqlBuilderAiQueryRelayResource,
		),
	],
	afterPaint: fg('jsm_remove_queue_bulk_action_bar_from_preloading')
		? [LazyIssueApp]
		: [LazyIssueApp, AsyncBulkActionToolbarLazy],
	// It is important to keep this component consistent across the routes so
	// that React doesn't unmount it when switching between the routes and the
	// full state is kept so that scroll position and loaded data is kept when
	// going back from issue details to the queues page.
	entryPoint() {
		return fg('csm_appification_standalone_navigation')
			? csmCustomQueuePageEntryPoint
			: notFoundPageEntrypoint;
	},

	isHydrateRootEnabled: false,
	forPaint: [LazyAtlassianNavigation],
	supportsPreviewPanel: true,
	bifrost: {
		bifrostGroup: 'jira-service-management',
		manifestName: 'jira-spa-jira-service-management.json',
		fetchTimeout: 1500,
	},
}));
